import { useEffect } from "react";
import type { OrganizationType } from "@bookdental/schemas";
import { cn } from "@/lib/cn";

type Minha = { role: string; organization: { id: string; name: string; types: OrganizationType[]; isPersonal: boolean } };

const O_QUE_VAI: Partial<Record<OrganizationType, string>> = {
  DENTAL_LAB: "catálogo (serviços, materiais, cores, fichas técnicas), equipamentos e insumos, preços, etapas de produção, etiquetas e prazo",
  DENTAL_OFFICE: "procedimentos, especialidades, agenda e expediente",
};

/**
 * "Começar do zero" ou "Copiar de outra organização" — só aparece com alguma organização do mesmo
 * tipo da qual a pessoa é dona. Pacientes, pedidos, estoque e equipe nunca vão (só os papéis).
 */
export function CopiarDeOutra({
  types,
  minhas,
  valor,
  onChange,
}: {
  types: OrganizationType[];
  minhas: Minha[];
  valor: string | null;
  onChange: (id: string | null) => void;
}) {
  const candidatas = minhas.filter((m) => m.role === "OWNER" && m.organization.types.some((t) => types.includes(t)));
  const valida = valor === null || candidatas.some((m) => m.organization.id === valor);
  // Trocou a área e a origem deixou de servir: volta pro "do zero" em vez de mandar uma origem inválida.
  useEffect(() => {
    if (!valida) onChange(null);
  }, [valida, onChange]);
  if (types.length === 0 || candidatas.length === 0) return null;
  const vai = types.flatMap((t) => (O_QUE_VAI[t] ? [O_QUE_VAI[t]] : [])).join("; ");

  const opcao = (ativo: boolean, titulo: string, texto: string, onClick: () => void) => (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={ativo}
      className={cn(
        "rounded-lg border p-3 text-left transition-colors",
        ativo ? "border-brand-600 bg-brand-50/60 ring-1 ring-brand-600 dark:bg-primary/10" : "border-zinc-200 hover:border-zinc-300 dark:border-border",
      )}
    >
      <span className="block text-sm font-semibold text-zinc-900 dark:text-foreground">{titulo}</span>
      <span className="block text-xs text-zinc-500 dark:text-muted-foreground">{texto}</span>
    </button>
  );

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium text-zinc-900 dark:text-foreground">Como começar</p>
      <div className="grid gap-2 sm:grid-cols-2">
        {opcao(
          valor === null,
          "Começar do zero",
          // Só o laboratório tem passo de onboarding; a clínica nasce com os procedimentos das especialidades.
          types.includes("DENTAL_LAB") ? "Você monta o catálogo no primeiro acesso." : "Com os procedimentos das especialidades que você marcar.",
          () => onChange(null),
        )}
        {opcao(valor !== null, "Copiar de outra organização", "Leva as mesmas configurações de uma que você já tem.", () =>
          onChange(valor ?? candidatas[0].organization.id),
        )}
      </div>
      {valor !== null && (
        <div className="space-y-1.5">
          <select
            value={valor}
            onChange={(e) => onChange(e.target.value)}
            aria-label="Organização de origem"
            className="h-9 w-full rounded-md border border-zinc-200 bg-white px-3 text-sm dark:border-border dark:bg-card"
          >
            {candidatas.map((m) => (
              <option key={m.organization.id} value={m.organization.id}>
                {m.organization.isPersonal ? `${m.organization.name} (pessoal)` : m.organization.name}
              </option>
            ))}
          </select>
          <p className="text-xs text-zinc-500 dark:text-muted-foreground">
            Vai junto: {vai}; e os papéis da equipe. Pacientes, pedidos, estoque e membros não — a equipe é convidada de novo.
          </p>
        </div>
      )}
    </div>
  );
}
